import { useEffect, useRef } from "react";
import { pollDelay, shouldFetchOnTick, shouldRefetchOnReturn } from "@/lib/polling";

/** Poll `fetcher` on an interval while the tab is visible.
 *
 *  Deliberately thin: every rule it follows lives in polling.js where it can be tested.
 *  This wrapper only owns the timer, the visibilitychange listener and the in-flight
 *  flag.
 *
 *  It does NOT touch the idle-logoff timer, and must never be changed to. See the note
 *  at the top of polling.js — an unattended screen that keeps itself logged in because
 *  it is polling is the failure that automatic logoff exists to prevent.
 */
export function usePolling(fetcher, intervalMs, { enabled = true } = {}) {
  const fetcherRef = useRef(fetcher);
  const inFlight = useRef(false);
  const timer = useRef(null);

  // Keep the latest callback without restarting the timer: a fetcher rebuilt on every
  // render (the normal useCallback-with-deps case) would otherwise clear and recreate
  // the interval constantly, and a 10s poll would effectively never fire.
  useEffect(() => { fetcherRef.current = fetcher; }, [fetcher]);

  useEffect(() => {
    const visible = () => typeof document === "undefined"
      || document.visibilityState !== "hidden";

    const run = async () => {
      if (!shouldFetchOnTick({ visible: visible(), enabled, inFlight: inFlight.current })) {
        return;
      }
      inFlight.current = true;
      try {
        await fetcherRef.current?.();
      } catch {
        // A failed poll is not an event: the screen already shows the last good data,
        // and a transient blip must not replace it with an error. The 401 that follows
        // an idle logoff lands here too, and the right response is to stay quiet and
        // let the logout redirect happen.
      } finally {
        inFlight.current = false;
      }
    };

    const stop = () => {
      if (timer.current) { clearInterval(timer.current); timer.current = null; }
    };

    const start = () => {
      stop();
      const delay = pollDelay({ visible: visible(), enabled, intervalMs });
      if (delay == null) return;        // hidden or disabled: hold no timer at all
      timer.current = setInterval(run, delay);
    };

    const onVisibilityChange = () => {
      if (visible()) {
        if (shouldRefetchOnReturn({ wasHidden: true, enabled })) run();
        start();
      } else {
        stop();
      }
    };

    start();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, enabled]);
}
